const { translateString, getElement, getElements } = window.BAO.Utils

/**
 * @typedef {{
 *   label: string;
 *   family: string;
 *   source: string;
 *   weight: string;
 * }} Font
 */

/**
 * @param {Font[]} fonts
 */
function loadFonts (fonts) {
  return Promise.all(
    fonts.map(font => {
      const fontFace = new FontFace(
        font.family,
        `url(${font.source}) format('woff2')`,
        { weight: font.weight },
      )

      return fontFace.load()
    }),
  )
}

class EngravingPreview extends window.BAO.CustomElement() {
  // static get requiredElements () {
  //   return ['text']
  // }

  /**
   * @param {{
   *  font: Font;
   *  text: string;
   * }} props
   */
  render ({ font, text, fontSize }) {
    this.style.setProperty('--EngravingPreview_Font', font.family)
    this.style.setProperty('--EngravingPreview_FontWeight', font.weight)

    this.renderLines(text, fontSize)
  }

  renderLines (text, fontSize = 20) {
    const svg = this.els.svg.element

    svg.innerHTML = ''

    // Example usage:
    const spreadedElements = this.spreadElements(text, fontSize)

    spreadedElements.forEach(element => {
      const SVG_NS = 'http://www.w3.org/2000/svg'

      const textContainer = document.createElementNS(SVG_NS, 'text')
      textContainer.classList.add('prd-EngravingPreview_EngravingText')
      textContainer.setAttribute('data-engraving-preview-el', 'text')
      textContainer.setAttribute('x', '50%')
      textContainer.setAttribute('y', element.position)
      textContainer.style.fontSize = `${fontSize}px`
      textContainer.textContent = element.element

      svg.appendChild(textContainer)
    })
  }

  spreadElements (arr, fontSize) {
    const spreadArray = []
    const totalElements = arr.length

    // Calculate the range for spreading around the center (50%)
    const gap = fontSize
    const centerPoint = parseInt(this.dataset.centerPoint)
    const spreadRange = (totalElements - 1) * gap // The maximum deviation from 50%
    const startPoint = centerPoint - spreadRange / 2

    for (let i = 0; i < totalElements; i++) {
      // Calculate spread index ensuring it's within 40% to 60%
      const position = startPoint + i * gap
      spreadArray.push({ element: arr[i], position })
    }

    return spreadArray
  }
}

if (!customElements.get('engraving-preview')) {
  customElements.define('engraving-preview', EngravingPreview)
}

class EngravingForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredElements () {
    return ['character-count', 'text-input']
  }

  connectedCallback () {
    super.connectedCallback()

    /**
     * @note switching to timeout due to issues with Safari
     * and window.requestIdleCallback()
     */
    // window.requestIdleCallback(() => {
    //   this.loadFonts()
    // })

    setTimeout(() => {
      this.loadFonts()
    }, 1000)

    // Add a click event listener to the reset button
    this.els.clear.element.addEventListener('click', event => {
      event.preventDefault()
      this.resetForm()
    })

    this.side = 'front'

    this.engraving = JSON.parse(this.dataset.engravingJson)
  }

  resetForm () {
    // Reset the text input
    this.els['text-input'].elements.forEach(element => {
      element.value = ''
      element.setAttribute('aria-invalid', false)
    })

    // Reset the font inputs
    this.els['font-input'].elements.forEach((input, index) => {
      input.checked = index === 0
    })

    // Reset the character count
    this.els['character-count'].element.textContent = '0'
    this.els['character-count'].element.dataset.characterLimitReached = false

    // Reset the font label
    this.els['font-label'].element.textContent = ''

    // Reset the drawer toggles
    this.updateDrawerToggles()

    // Reset the previews
    this.updatePreviews()

    // Reset engraving choices
    const engravingText = document.querySelector(
      '[data-product-form-el="engraving-text"]',
    )
    if (engravingText) {
      engravingText.innerHTML = ''
    }

    const engravingFont = document.querySelector(
      '[data-product-form-el="engraving-font"]',
    )
    if (engravingFont) {
      engravingFont.value = ''
    }

    // Reset icons on engraving drawer trigger
    const variantDrawerToggleCheck = document.querySelector(
      '.prd-DetailVariants_VariantDrawerToggle-check',
    )
    variantDrawerToggleCheck?.classList.add(
      'prd-DetailVariants_VariantDrawerToggle-hidden',
    )

    const variantDrawerToggleEdit = document.querySelector(
      '.prd-DetailVariants_VariantDrawerToggle-edit',
    )
    variantDrawerToggleEdit?.classList.add(
      'prd-DetailVariants_VariantDrawerToggle-hidden',
    )

    const submitEl = this.externalEls.submit.elements.find(
      el =>
        this.dataset.productId === el.dataset.productId &&
        this.dataset.groupId === el.dataset.groupId,
    )
    if (submitEl) {
      submitEl.disabled = true
    }
  }

  setupListeners () {
    super.setupListeners()

    this.els['text-input'].elements.forEach(input => {
      this.listeners.add(input, 'input', event => this.handleTextInput(event))
    })

    this.els['font-input'].elements.forEach(input => {
      this.listeners.add(input, 'input', event => this.handleFontChange(event))
    })
    this.listeners.add(this, 'input', () => this.updatePreviews())
    this.listeners.add(this, 'submit', event => this.handleSubmit(event))

    this.els.side_switcher?.elements.forEach(button => {
      this.listeners.add(button, 'click', event => {
        this.side = event.target.dataset.sideSelect
      })
    })

    this.els['new-line']?.elements.forEach(input => {
      this.listeners.add(input, 'click', event => this.newLine(event))
    })
  }

  removeLine () {
    const engraving = this.engraving
    engraving[this.side].currentLines--

    this.engraving = engraving

    this.renderLines()
    this.updatePreviews()
  }

  newLine () {
    const engraving = this.engraving
    engraving[this.side].currentLines++

    this.engraving = engraving

    this.renderLines()
    this.updatePreviews()
  }

  textInputListener () {
    const linesEl = this.els.lines.elements.find(
      element => element.dataset.side === this.side,
    )

    const textEls = getElements(this.els['text-input'].selector, {
      context: linesEl,
    })
    textEls.forEach(input => {
      this.listeners.add(input, 'input', event => this.handleTextInput(event))
    })

    const removeEls = getElements(this.els.remove_line?.selector, {
      context: linesEl,
    })
    removeEls.forEach(element => {
      this.listeners.add(element, 'click', event => this.removeLine(event))
    })

    this.els['text-input'].elements.forEach(input => {
      input.dispatchEvent(new Event('input'))
    })
  }

  /** @param {InputEvent} event */
  handleFontChange (event) {
    this.updateFontLabel(event.target.value)
  }

  /** @param {SubmitEvent} event */
  handleSubmit (event) {
    event.preventDefault()
    const hasInputError =
      this.els['text-input'].element.getAttribute('aria-invalid') === 'true'

    if (hasInputError) {
      return
    }

    this.applyEngraving()
    this.updateDrawerToggles()

    if (this.dataset.fallbackDrawer !== '') {
      document.querySelector('site-drawers').open(this.dataset.fallbackDrawer)
    } else {
      document.querySelector('site-drawers').close()
    }
  }

  /** @param {InputEvent} event */
  handleTextInput (event) {
    this.updateMaxLength(event)
    this.validateTextInput(event)
    this.updateCharacterCount(event)
    this.updateObject(event)
    this.updateSubmitState(event)
  }

  updateObject (event) {
    const engraving = this.engraving
    const line = event.target.dataset.index
    const value = event.target.value

    engraving[this.side].lines[line - 1].value = value

    this.engraving = engraving
  }

  /** @param {InputEvent} event */
  validateTextInput (event) {
    /**
     * @param {number} limit
     * @returns {(string: string) => string}
     */
    function limitCharacterCount (limit) {
      return function (string) {
        return string.substring(0, limit)
      }
    }

    /**
     * Pass a series of functions to transform the string, then pass the string you wish to transform
     *
     * @example
     *   applyStringTransforms(
     *    removeCharacters(['!']),
     *    toUpperCase,
     *    limitCharacters(8),
     *  )('transform me!')
     *
     * @template {string}
     *
     * @param {...(value: string) => string} functions
     * @returns {(value: string) => string}
     */
    function applyStringTransforms (...functions) {
      return function (value) {
        return functions.reduce((accumulator, fn) => fn(accumulator), value)
      }
    }

    /**
     * @note Pass a function into `applyStringTransforms` for every change you wish to make
     * to the input value. `applyStringTransforms` will then apply those changes in order
     */
    event.target.value = applyStringTransforms(
      limitCharacterCount(event.target.maxLength),
    )(event.target.value)
  }

  renderLines () {
    const side = this.side
    const linesCount = this.engraving[side].currentLines - 1
    const linesContainer = this.els.lines.elements.find(
      element => element.dataset.side === side,
    )
    linesContainer.innerHTML = ''

    for (let index = 0; index < linesCount; index++) {
      let lineTemplate = this.els['line-template'].element.innerHTML

      this.els['new-line'].elements.forEach(element => {
        if (element.dataset.side === side) {
          element.dataset.maxLines =
            this.engraving[side].currentLines === this.engraving[side].maxLines
        }
      })

      lineTemplate = lineTemplate.replaceAll('{SIDE}', side)
      lineTemplate = lineTemplate.replaceAll(
        '{LIMIT}',
        this.engraving[side].lines[linesCount].maxLength,
      )
      lineTemplate = lineTemplate.replaceAll('{INDEX}', index + 2)

      lineTemplate = lineTemplate.replaceAll(
        '{VALUE}',
        this.engraving[side].lines[index + 1].value,
      )

      const node = document.createElement('div')
      node.classList.add('prd-EngravingForm_LineOuter')
      node.innerHTML = lineTemplate

      linesContainer.append(node)
    }

    this.textInputListener()
  }

  updateMaxLength (event) {
    const side = event.target.dataset.side
    const currentLines = this.engraving[side].currentLines
    const characterLimit = this.engraving[side].lines[currentLines - 1]
      ?.maxLength

    event.target.maxLength = characterLimit
  }

  updateCharacterCount (event) {
    const side = event.target.dataset.side
    const currentLines = this.engraving[side].currentLines
    const textLength = event.target.value.length
    const characterLimit = this.engraving[side].lines[currentLines - 1]
      .maxLength
    const characterCountSide = event.target.parentElement.previousSibling

    characterCountSide.textContent = translateString(
      this.dataset.characterCountTemplate,
      {
        current: textLength,
        total: characterLimit,
      },
    )

    characterCountSide.dataset.characterLimitReached =
      textLength >= characterLimit
  }

  updateSubmitState () {
    const submitEl = this.externalEls.submit.elements.find(
      el =>
        this.dataset.productId === el.dataset.productId &&
        this.dataset.groupId === el.dataset.groupId,
    )

    let hasFrontData = false
    let hasBackData = false
    let hasData = false
    this.els['text-input'].elements.forEach(element => {
      if (element.dataset.side === 'front' && element.value.length > 0) {
        hasFrontData = true
      }

      if (element.dataset.side === 'back' && element.value.length > 0) {
        hasBackData = true
      }
    })

    if (hasFrontData || hasBackData) {
      hasData = true
    }

    if (submitEl) {
      submitEl.disabled = !hasData
    }
  }

  /** @param {string} font */
  updateFontLabel (font) {
    this.els['font-label'].element.textContent = translateString(
      this.dataset.fontLabelTemplate,
      {
        font,
      },
    )
  }

  updatePreviews () {
    const formData = new FormData(this)

    const previewEl = this.externalEls.preview.elements.filter(
      el =>
        this.dataset.productId === el.dataset.productId &&
        this.dataset.groupId === el.dataset.groupId,
    )

    if (previewEl) {
      previewEl.forEach(preview => {
        let text = []
        const currentLine = this.engraving[preview.dataset.side].currentLines

        const fontSize = this.engraving[preview.dataset.side].lines[
          currentLine - 1
        ]?.fontSize

        for (const pair of formData.entries()) {
          if (pair[0].includes(`text_${preview.dataset.side}`)) {
            text = [...text, pair[1]]
          }
        }

        const props = {
          font: this.selectedFont,
          text,
          fontSize,
        }

        preview.render(props)
      })
    }
  }

  updateDrawerToggles () {
    const formData = new FormData(this)

    let lines = []

    for (const pair of formData.entries()) {
      if (pair[0].includes('text_') && pair[1] !== '') {
        lines = [...lines, pair[1]]
      }
    }

    const text = lines.join(',')
    const { addEngravingTemplate, engravingAddedTemplate } = this.dataset
    const labelTemplate =
      text.length > 0 ? engravingAddedTemplate : addEngravingTemplate

    const toggleLabels = this.externalEls['toggle-label'].elements.filter(
      label =>
        label.dataset.productId === this.dataset.productId &&
        label.dataset.groupId === this.dataset.groupId,
    )
    toggleLabels.forEach(toggleLabel => {
      toggleLabel.innerHTML = labelTemplate
    })

    const engravingTextLabels = this.externalEls[
      'engraving-text'
    ]?.elements.filter(
      label => this.dataset.productId === label.dataset.productId,
    )
    engravingTextLabels?.forEach(engravingTextLabel => {
      engravingTextLabel.innerHTML = text
    })

    const toggleEl = this.externalEls.toggle?.elements.filter(
      label => this.dataset.productId === label.dataset.productId,
    )
    toggleEl?.forEach(toggle => {
      toggle.classList.toggle(
        'prd-DetailVariants_Variant-text',
        text.length > 0,
      )

      const checkIconContainer = toggle.querySelector(
        '.prd-DetailVariants_VariantDrawerToggle-check',
      )
      const editIconContainer = toggle.querySelector(
        '.prd-DetailVariants_VariantDrawerToggle-edit',
      )

      checkIconContainer.classList =
        'prd-DetailVariants_VariantDrawerToggle-check'
      editIconContainer.classList =
        'prd-DetailVariants_VariantDrawerToggle-edit'
    })
  }

  applyEngraving () {
    const formData = new FormData(this)
    let lines = []

    for (const pair of formData.entries()) {
      if (pair[0].includes('text_')) {
        lines = [...lines, pair]
      }
    }

    const text = lines
    const font = text.length > 0 ? formData.get('font') : ''
    const productId = this.dataset.productId

    if (this.dataset.for === 'charms') {
      const charmEl = getElement(`[data-charm-id="${this.dataset.productId}"]`)

      charmEl.dataset.engravingText = JSON.stringify(lines)
      charmEl.dataset.engravingFont = font

      this.dispatchEvent(
        new CustomEvent('product-engraving:update-charms', {
          bubbles: true,
          detail: {
            font,
            text,
            productId,
          },
        }),
      )
    } else if (this.dataset.for === 'bundle_set') {
      const charmEl = getElement(
        `[data-bundle-set-external-el="charm_option"][data-product-id="${this.dataset.productId}"][data-group-id="${this.dataset.groupId}"]`,
      )

      charmEl.dataset.engravingText = JSON.stringify(lines)
      charmEl.dataset.engravingFont = font

      this.dispatchEvent(
        new CustomEvent('bundle-set:update-charms', {
          bubbles: true,
          detail: {
            font,
            text,
            productId,
          },
        }),
      )
    } else {
      this.dispatchEvent(
        new CustomEvent('product-engraving:set', {
          bubbles: true,
          detail: {
            font,
            text,
            formId: this.dataset.for,
          },
        }),
      )
    }
  }

  loadFonts () {
    loadFonts(this.availableFonts).then(fonts => {
      fonts.forEach(font => {
        document.fonts.add(font)
      })
    })
  }

  /** @returns {Font[]} */
  get availableFonts () {
    return JSON.parse(this.dataset.fonts)
  }

  /** @returns {HTMLInputElement} */
  get selectedFontInput () {
    return this.els['font-input'].elements.find(element => element.checked)
  }

  get selectedFont () {
    return this.availableFonts.find(
      font => font.label === this.selectedFontInput.value,
    )
  }

  /**
   * Change the charm position
   */
  get engraving () {
    return this._engraving
  }

  set engraving (engraving) {
    this._engraving = engraving
  }

  /**
   * Change the side shown
   */
  get side () {
    return this._side
  }

  set side (side) {
    const sideEls = getElements(`[data-side]`, { context: this })
    sideEls.forEach(el => {
      el.ariaHidden = side !== el.dataset.side
    })

    this.externalEls.preview?.elements.forEach(el => {
      el.ariaHidden = side !== el.dataset.side
    })

    this.els.side_switcher?.elements.forEach(el => {
      el.ariaSelected = side === el.dataset.sideSelect
    })

    this._side = side
  }
}

if (!customElements.get('engraving-form')) {
  customElements.define('engraving-form', EngravingForm, { extends: 'form' })
}

class EngravingTextInput extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['input', 'error']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.input.element,
      'input',
      this.handleTextInput.bind(this),
    )
  }

  /** @param {InputEvent} event */
  handleTextInput (event) {
    this.validate(event.currentTarget.value)
  }

  /** @param {string} value */
  validate (value) {
    const failedTest = this.rules.find(rule => !rule.test(value))
    const hasError = Boolean(failedTest)

    this.els.input.element.setAttribute('aria-invalid', hasError)
    this.els.error.element.setAttribute('aria-hidden', !hasError)
    this.els.error.element.textContent = failedTest?.errorMessage ?? ''
  }

  /**
   * @typedef {Object} InputValidationRule
   *
   * @property {string} id
   * @property {string} errorMessage
   * @property {(value: string) => boolean} test
   */

  /** @returns {InputValidationRule[]} */
  get rules () {
    /** @type {Record<string, string>} */
    const errorMessages = JSON.parse(this.dataset.errorMessages)

    return [
      {
        id: 'alphanumerical-characters-only',
        test (value) {
          const pattern = /[^\w!--$(). ]/g

          return !pattern.test(value)
        },
      },
    ].map(rule => {
      return {
        ...rule,
        errorMessage: errorMessages[rule.id] || '',
      }
    })
  }
}

if (!customElements.get('engraving-text-input')) {
  customElements.define('engraving-text-input', EngravingTextInput)
}
